import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken } from '@angular/core';
import { catchError, Observable, of, shareReplay, tap } from 'rxjs';
import {
  ILoginRequest,
  ILoginResponse,
  IOtpLoginRequest,
  IOtpLoginResponse, IOTPVerificationRequest, IOTPVerificationResponse,
  IRecoveryCodesResponse,
  IChangeOwnPasswordRequest,
  IResetPassword,
  IResetPasswordOtp,
  ISuccessulResponse,
  ITotpEnrollResponse,
  ITwoFactorStatus,
  ITwoFactorStepUpRequest,
} from '@inshuwa-client/models';
import { Router } from '@angular/router';
import { baseUrl } from '../configs/baseUrl';
import { rememberOtpContact } from '../utils/otp-contact';
import { setPermissions, setStoredLoginResponse } from '../utils/auth-storage';
import { endSession } from '../utils/session-store';
import { GetDecodedToken } from '../utils/token-decode';
import { forgetWebPush } from '../utils/web-push';
import { UmamiService } from './umami.service';

@Injectable({
  providedIn: 'root'
})
export class SharedAuthService {
  constructor(
    private http: HttpClient,
    readonly router: Router
  ) {
  }

  static identifyUserInUmami() {
    const decodedToken = GetDecodedToken();
    if (decodedToken && decodedToken.id) {
      UmamiService.identify(decodedToken.id);
    }
  }

  static identifyUserInSentry() {
    const decodedToken = GetDecodedToken();
    if (decodedToken && decodedToken.id) {
      import('@sentry/angular').then((Sentry) => {
        Sentry.setUser({
          id: decodedToken.id,
          email: decodedToken.email
        });
      });
    }
  }

  public clientLogin(
    login: Partial<ILoginRequest>
  ): Observable<IOTPVerificationResponse> {
    return this.http
      .post<IOTPVerificationResponse>(`${baseUrl()}/auth/clients/login`, login)
      .pipe(
        tap((res) => {
          if (!res.isTwoFactorRequired) {
            setStoredLoginResponse(res as ILoginResponse);
          }
        })
      );
  }

  public selectAccount(selectionToken: string, userId: number): Observable<ILoginResponse> {
    return this.http.post<ILoginResponse>(`${baseUrl()}/auth/accounts/select`, {
      selectionToken,
      userId,
    });
  }

  public clientOTPVerification(codeRequest: IOTPVerificationRequest): Observable<IOTPVerificationResponse> {
    return this.http.post<IOTPVerificationResponse>(
      `${baseUrl()}/auth/2fa/verify`,
      codeRequest,
    );
  }

  public clientOtpLogin(
    login: IOtpLoginRequest
  ): Observable<IOtpLoginResponse> {
    rememberOtpContact(login.phoneNumber, login.email);
    return this.http.post<IOtpLoginResponse>(
      `${baseUrl()}/auth/clients/otp-login`,
      login
    );
  }

  /** everywhere=false signs out only this tab when per-tab sessions are on (see session-store). */
  public logOut(everywhere = true) {
    forgetWebPush();
    endSession(everywhere);
    this.router.navigate(['/'], { replaceUrl: true }).then((value) => {
      if (value) {
        window.location.reload();
      }
    });
  }

  public refreshToken(
    userId: string,
    refreshToken: string
  ): Observable<ILoginResponse> {
    return this.http
      .post<ILoginResponse>(`${baseUrl()}/auth/refresh-token`, {
        userId: userId,
        token: refreshToken
      })
      .pipe(
        tap((res) => {
          setStoredLoginResponse(res);
        })
      );
  }

  public getTwoFactorStatus(): Observable<ITwoFactorStatus> {
    return this.http.get<ITwoFactorStatus>(`${baseUrl()}/auth/me/2fa`);
  }

  public enableTwoFactor(): Observable<ISuccessulResponse> {
    return this.http.post<ISuccessulResponse>(
      `${baseUrl()}/auth/me/2fa/enable`,
      {},
    );
  }

  public disableTwoFactor(
    request: ITwoFactorStepUpRequest,
  ): Observable<ISuccessulResponse> {
    return this.http.post<ISuccessulResponse>(
      `${baseUrl()}/auth/me/2fa/disable`,
      request,
    );
  }

  public enrollTotp(): Observable<ITotpEnrollResponse> {
    return this.http.post<ITotpEnrollResponse>(
      `${baseUrl()}/auth/me/2fa/totp/enroll`,
      {},
    );
  }

  public confirmTotp(code: string): Observable<IRecoveryCodesResponse> {
    return this.http.post<IRecoveryCodesResponse>(
      `${baseUrl()}/auth/me/2fa/totp/confirm`,
      { code },
    );
  }

  public regenerateRecoveryCodes(
    request: ITwoFactorStepUpRequest,
  ): Observable<IRecoveryCodesResponse> {
    return this.http.post<IRecoveryCodesResponse>(
      `${baseUrl()}/auth/me/2fa/recovery-codes/regenerate`,
      request,
    );
  }

  public changeOwnPassword(
    request: IChangeOwnPasswordRequest,
  ): Observable<ISuccessulResponse> {
    return this.http.post<ISuccessulResponse>(
      `${baseUrl()}/auth/me/password/change`,
      request,
    );
  }

  public getMyPermissions(): Observable<Record<string, boolean>> {
    return this.http
      .get<Record<string, boolean>>(`${baseUrl()}/auth/me/permissions`)
      .pipe(
        catchError((error) => {
          console.error('Error fetching permissions:', error);
          return of({});
        }),
        shareReplay(1),
        tap((value) => {
          setPermissions(value);
        })
      );
  }
}

export const AuthService = new InjectionToken<IAuthService>('AUTH_SERVICE');

export interface IAuthService {
  login(login: Partial<ILoginRequest>): Observable<ILoginResponse>;

  resetPassword(login: IResetPassword): Observable<ISuccessulResponse>;

  requestPasswordResetOtp(
    login: IResetPasswordOtp
  ): Observable<ISuccessulResponse>;
}
